import React, { useEffect, useState } from "react"
import axios from "axios"

const STATUS_COLORS = {
  success: { bg: "#f0fdf4", color: "#15803d" },
  danger: { bg: "#fef2f2", color: "#b91c1c" },
  method: { bg: "#eff6ff", color: "#1d4ed8" },
}

const Badge = ({ type, children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 100,
      fontSize: 11,
      fontWeight: 600,
      background: STATUS_COLORS[type]?.bg,
      color: STATUS_COLORS[type]?.color,
    }}
  >
    {children}
  </span>
)

const fmtDate = dt => {
  try {
    return new Date(dt).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    })
  } catch {
    return dt || "—"
  }
}

const fmtJson = data => {
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return "Invalid JSON"
  }
}

const PAGE_SIZE = 10

const ApiLogs = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [search, setSearch] = useState("")
  const [methodFilter, setMethodFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  const [selectedLog, setSelectedLog] = useState(null)
  const [activeTab, setActiveTab] = useState("summary")
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchLogs(1)
  }, [])

  const fetchLogs = async (p = 1) => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get(
        `https://ats.bharatpayroll.com/qxbox/api/v1/api-log/?page=${p}`
      )
      const data = response.data
      setLogs(data.results || [])
      setTotalCount(data.count || 0)
      setTotalPages(Math.ceil((data.count || 0) / PAGE_SIZE) || 1)
      setPage(p)
    } catch (err) {
      setError("Failed to load logs. Please check your connection.")
      setLogs([])
    } finally {
      setLoading(false)
    }
  }

  const filteredLogs = logs.filter(log => {
    const txt = [log.api_name, log.method, log.status_code, log.url]
      .join(" ")
      .toLowerCase()
    if (search && !txt.includes(search.toLowerCase())) return false
    if (methodFilter !== "all" && log.method !== methodFilter) return false
    if (
      statusFilter === "success" &&
      !(log.status_code >= 200 && log.status_code < 300)
    )
      return false
    if (statusFilter === "failed" && !(log.status_code >= 400)) return false
    return true
  })

  const methods = [...new Set(logs.map(l => l.method).filter(Boolean))]

  const successCount = logs.filter(
    l => l.status_code >= 200 && l.status_code < 300
  ).length
  const failedCount = logs.filter(l => l.status_code >= 400).length
  const successRate = logs.length
    ? ((successCount / logs.length) * 100).toFixed(1)
    : 0

  const openLog = log => {
    setSelectedLog(log)
    setActiveTab("summary")
    setCopied(false)
  }

  const copyJson = data => {
    navigator.clipboard.writeText(fmtJson(data)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const getPaginationPages = () => {
    const pages = []
    const lo = Math.max(1, page - 2)
    const hi = Math.min(totalPages, page + 2)
    if (lo > 1) {
      pages.push(1)
      if (lo > 2) pages.push("...")
    }
    for (let i = lo; i <= hi; i++) pages.push(i)
    if (hi < totalPages) {
      if (hi < totalPages - 1) pages.push("...")
      pages.push(totalPages)
    }
    return pages
  }

  const statCards = [
    {
      label: "Total logs",
      value: totalCount.toLocaleString(),
      color: "#1e40af",
      pct: 100,
    },
    {
      label: "Successful",
      value: successCount,
      color: "#15803d",
      pct: logs.length ? (successCount / logs.length) * 100 : 0,
    },
    {
      label: "Failed",
      value: failedCount,
      color: "#b91c1c",
      pct: logs.length ? (failedCount / logs.length) * 100 : 0,
    },
    {
      label: "Success rate",
      value: `${successRate}%`,
      color: "#1e40af",
      pct: successRate,
    },
  ]

  return (
    <div
      style={{ padding: "1.5rem", background: "#f8fafc", minHeight: "100vh" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: "#0f172a",
              margin: 0,
            }}
          >
            API Logs
          </h1>
          <p style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
            Page {page} of {totalPages} — {totalCount.toLocaleString()} total
            entries
          </p>
        </div>
        <button
          onClick={() => fetchLogs(page)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background: "#1e40af",
            color: "#fff",
            fontWeight: 500,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: "1.5rem",
        }}
      >
        {statCards.map(s => (
          <div
            key={s.label}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "1rem 1.25rem",
              border: "0.5px solid #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 6,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 600, color: s.color }}>
              {s.value}
            </div>
            <div
              style={{
                height: 3,
                background: "#f1f5f9",
                borderRadius: 2,
                marginTop: 10,
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 2,
                  background: s.color,
                  width: `${s.pct}%`,
                  transition: "width 0.4s",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div
        style={{
          background: "#fff",
          border: "0.5px solid #e2e8f0",
          borderRadius: 12,
          padding: "0.75rem 1rem",
          marginBottom: "1rem",
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative", flex: 1 }}>
          <span
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: 15,
            }}
          >
            🔍
          </span>
          <input
            type="text"
            placeholder="Search by API name, method, URL, status…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px 8px 32px",
              border: "0.5px solid #cbd5e1",
              borderRadius: 8,
              background: "#f8fafc",
              fontSize: 13,
              color: "#0f172a",
              outline: "none",
            }}
          />
        </div>
        <select
          value={methodFilter}
          onChange={e => setMethodFilter(e.target.value)}
          style={{
            padding: "8px 10px",
            border: "0.5px solid #cbd5e1",
            borderRadius: 8,
            background: "#f8fafc",
            fontSize: 13,
            color: "#0f172a",
            cursor: "pointer",
          }}
        >
          <option value="all">All methods</option>
          {methods.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: "8px 10px",
            border: "0.5px solid #cbd5e1",
            borderRadius: 8,
            background: "#f8fafc",
            fontSize: 13,
            color: "#0f172a",
            cursor: "pointer",
          }}
        >
          <option value="all">All statuses</option>
          <option value="success">Success (2xx)</option>
          <option value="failed">Failed (4xx+)</option>
        </select>
      </div>

      {/* Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "0.5px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr
              style={{
                background: "#f8fafc",
                borderBottom: "0.5px solid #e2e8f0",
              }}
            >
              {["#", "API name", "Method", "Status", "Created", ""].map(h => (
                <th
                  key={h}
                  style={{
                    padding: "10px 14px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#64748b",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}
                >
                  Loading logs…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: 40, color: "#b91c1c" }}
                >
                  {error}
                </td>
              </tr>
            ) : filteredLogs.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}
                >
                  No logs match your filters
                </td>
              </tr>
            ) : (
              filteredLogs.map(log => (
                <tr
                  key={log.id}
                  style={{
                    borderBottom: "0.5px solid #f1f5f9",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.background = "#f8fafc")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <td
                    style={{
                      padding: "12px 14px",
                      color: "#94a3b8",
                      fontSize: 12,
                    }}
                  >
                    {log.id}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>
                      {log.api_name || "—"}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        marginTop: 2,
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.url}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <Badge type="method">{log.method}</Badge>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {log.status_code >= 200 && log.status_code < 300 ? (
                      <Badge type="success">✓ {log.status_code}</Badge>
                    ) : (
                      <Badge type="danger">✕ {log.status_code}</Badge>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "12px 14px",
                      fontSize: 12,
                      color: "#64748b",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtDate(log.created_at)}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <button
                      onClick={() => openLog(log)}
                      style={{
                        padding: "4px 12px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "0.5px solid #cbd5e1",
                        background: "transparent",
                        color: "#0f172a",
                        cursor: "pointer",
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && !error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              borderTop: "0.5px solid #e2e8f0",
            }}
          >
            <span style={{ fontSize: 12, color: "#64748b" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, totalCount)} of{" "}
              {totalCount.toLocaleString()} logs
            </span>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <PageBtn disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>
                ‹
              </PageBtn>
              {getPaginationPages().map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    style={{ padding: "0 4px", color: "#94a3b8", fontSize: 12 }}
                  >
                    …
                  </span>
                ) : (
                  <PageBtn
                    key={p}
                    active={p === page}
                    onClick={() => fetchLogs(p)}
                  >
                    {p}
                  </PageBtn>
                )
              )}
              <PageBtn
                disabled={page >= totalPages}
                onClick={() => fetchLogs(page + 1)}
              >
                ›
              </PageBtn>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedLog && (
        <div
          onClick={() => setSelectedLog(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
            zIndex: 1000,
            overflowY: "auto",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 14,
              width: 700,
              maxHeight: "80vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              border: "0.5px solid #e2e8f0",
            }}
          >
            {/* Modal header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 18px",
                borderBottom: "0.5px solid #e2e8f0",
              }}
            >
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                Log #{selectedLog.id} — {selectedLog.api_name}
              </h2>
              <button
                onClick={() => setSelectedLog(null)}
                style={{
                  width: 28,
                  height: 28,
                  border: "none",
                  background: "#f1f5f9",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal body */}
            <div style={{ overflowY: "auto", padding: "16px 18px", flex: 1 }}>
              {/* Meta grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                {[
                  { label: "API name", value: selectedLog.api_name },
                  { label: "Method", value: selectedLog.method },
                  { label: "Status", value: selectedLog.status_code },
                  { label: "Created", value: fmtDate(selectedLog.created_at) },
                ].map(m => (
                  <div
                    key={m.label}
                    style={{
                      background: "#f8fafc",
                      borderRadius: 8,
                      padding: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {m.label}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        marginTop: 4,
                        wordBreak: "break-all",
                      }}
                    >
                      {m.value || "—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "0.5px solid #e2e8f0",
                  marginBottom: 14,
                }}
              >
                {["summary", "request", "response", "error"].map(t => (
                  <button
                    key={t}
                    onClick={() => {
                      setActiveTab(t)
                      setCopied(false)
                    }}
                    style={{
                      padding: "8px 14px",
                      fontSize: 13,
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      borderBottom:
                        activeTab === t
                          ? "2px solid #1e40af"
                          : "2px solid transparent",
                      color: activeTab === t ? "#1e40af" : "#64748b",
                      fontWeight: activeTab === t ? 600 : 400,
                      transition: "all 0.15s",
                    }}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {activeTab === "summary" && (
                <div>
                  {[
                    { key: "URL", val: selectedLog.url },
                    { key: "Success", val: selectedLog.success ? "Yes" : "No" },
                    { key: "Error", val: selectedLog.error_message || "None" },
                  ].map(f => (
                    <div
                      key={f.key}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: "10px 0",
                        borderBottom: "0.5px solid #f1f5f9",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: "#64748b",
                          minWidth: 80,
                          flexShrink: 0,
                        }}
                      >
                        {f.key}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color:
                            f.key === "Error" && selectedLog.error_message
                              ? "#b91c1c"
                              : "#0f172a",
                          wordBreak: "break-all",
                        }}
                      >
                        {f.val}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "request" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 8,
                    }}
                  >
                    <button
                      onClick={() => copyJson(selectedLog.request_payload)}
                      style={{
                        padding: "4px 12px",
                        fontSize: 12,
                        border: "0.5px solid #cbd5e1",
                        borderRadius: 6,
                        background: "#f8fafc",
                        color: "#0f172a",
                        cursor: "pointer",
                      }}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre
                    style={{
                      background: "#0f172a",
                      color: "#e2e8f0",
                      padding: 14,
                      borderRadius: 8,
                      fontSize: 12,
                      fontFamily: "monospace",
                      overflowX: "auto",
                      maxHeight: 380,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {fmtJson(selectedLog.request_payload)}
                  </pre>
                </>
              )}

              {activeTab === "response" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 8,
                    }}
                  >
                    <button
                      onClick={() => copyJson(selectedLog.response_body)}
                      style={{
                        padding: "4px 12px",
                        fontSize: 12,
                        border: "0.5px solid #cbd5e1",
                        borderRadius: 6,
                        background: "#f8fafc",
                        color: "#0f172a",
                        cursor: "pointer",
                      }}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre
                    style={{
                      background: "#0f172a",
                      color: "#e2e8f0",
                      padding: 14,
                      borderRadius: 8,
                      fontSize: 12,
                      fontFamily: "monospace",
                      overflowX: "auto",
                      maxHeight: 380,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {fmtJson(selectedLog.response_body)}
                  </pre>
                </>
              )}

              {activeTab === "error" && (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    background: selectedLog.error_message
                      ? "#fef2f2"
                      : "#f0fdf4",
                    color: selectedLog.error_message ? "#b91c1c" : "#15803d",
                  }}
                >
                  {selectedLog.error_message || "No errors found"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const PageBtn = ({ children, active, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: 30,
      height: 30,
      borderRadius: 6,
      fontSize: 13,
      border: "0.5px solid #e2e8f0",
      background: active ? "#1e40af" : "transparent",
      color: active ? "#fff" : "#0f172a",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: active ? 600 : 400,
      transition: "all 0.15s",
    }}
  >
    {children}
  </button>
)

export default ApiLogs
